import React from 'react'
import {
  PiArrowLeft,
  PiCaretDown,
  PiCaretLeft,
  PiCaretRight,
  PiToolbox,
} from 'react-icons/pi'
import { cn } from 'components/UI/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from 'styles/z-index'
import { ComposeMode } from './ComposePanel'
import { useEmailThreadActions } from './emailActions'

interface EmailDetailActionBarProps {
  threadIndex: number
  totalThreads: number
  pageSize: number
  pageStart: number
  onBack: () => void
  onNavigateThread: (delta: number) => void
  onCompose?: (mode: ComposeMode) => void
}

/** Action bar for the email detail view with back button, actions dropdown, and pagination. */
export function EmailDetailActionBar({
  threadIndex,
  totalThreads,
  pageSize,
  pageStart,
  onBack,
  onNavigateThread,
  onCompose,
}: EmailDetailActionBarProps) {
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  const actions = useEmailThreadActions(onCompose)

  const pageEnd = Math.min(pageStart + pageSize - 1, totalThreads)
  const hasPrev = threadIndex > 0
  const hasNext = threadIndex < pageEnd - pageStart

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          className="flex items-center justify-center size-8 rounded hover:bg-[#F3F3F3] text-[#6E6E6E] transition-colors"
          title="Back to list"
        >
          <PiArrowLeft className="size-5" />
        </button>

        {/* Actions dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 h-8 px-1.5 rounded hover:bg-[#F3F3F3] text-sm font-medium text-[#6E6E6E] hover:text-[#191D23] transition-colors"
            >
              <PiToolbox className="size-6" />
              <span>Actions</span>
              <PiCaretDown className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            style={{ minWidth: 200, zIndex }}
            className="bg-white border-[#8EA69B]"
          >
            {actions.map((action, index) => (
              <React.Fragment key={index}>
                <DropdownMenuItem
                  disabled={action.disabled}
                  onSelect={action.disabled ? undefined : action.onClick}
                  className={cn(
                    'flex items-center gap-2',
                    action.disabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer'
                  )}
                >
                  <action.icon className="size-4 flex-shrink-0 text-gray-500" />
                  <span>{action.label}</span>
                </DropdownMenuItem>
                {action.hasSeparatorAfter && <DropdownMenuSeparator />}
              </React.Fragment>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Pagination */}
      <div className="flex items-center gap-3 text-sm text-[#6E6E6E]">
        <span>
          {totalThreads > 0 ? `${pageStart}\u2013${pageEnd}` : '0'} of{' '}
          {totalThreads.toLocaleString()}
        </span>
        <button
          type="button"
          disabled={!hasPrev}
          onClick={() => onNavigateThread(-1)}
          className={cn(
            'p-1 rounded transition-colors',
            hasPrev
              ? 'hover:bg-[#F3F3F3] text-[#6E6E6E]'
              : 'text-[#D0D0D0] cursor-not-allowed'
          )}
        >
          <PiCaretLeft className="size-5" />
        </button>
        <button
          type="button"
          disabled={!hasNext}
          onClick={() => onNavigateThread(1)}
          className={cn(
            'p-1 rounded transition-colors',
            hasNext
              ? 'hover:bg-[#F3F3F3] text-[#6E6E6E]'
              : 'text-[#D0D0D0] cursor-not-allowed'
          )}
        >
          <PiCaretRight className="size-5" />
        </button>
      </div>
    </div>
  )
}
